import { useState, useMemo, useEffect } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  JobPhaseApproveDto,
  JobPhaseTimelineResponseDto,
  PhaseTypeEnum,
  PhaseStructureTypeEnum,
} from '@vatos-pas/common';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';
import { MasterSheetContainer } from '../../containers/useMasterSheetContainer';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_REQUEST_RESOURCE,
  CAN_REQUEST_RESOURCE_ASSIGNED,
} from 'providers/permissions';
import { dateWithoutServerTimezone } from '../../utils/dateWithoutServerTimezone';
import {
  SKIPPED_PHASE_DISPLAY,
  getPhaseByType,
  isStartingApprovedPhase,
} from 'utils/phaseUtils';
import {
  getPhasesForJobType,
  getModalConfig,
  BUMP_PHASES,
  isTrimOptionPhase,
} from '../../constants/phaseConfigurations';
import {
  isExteriorPaintPhase,
  isPhaseIncluded,
} from 'utils/optionalWorkflowPhases';
import { useWorkflowConfig } from '../../config';
import { PhaseApprovalOptionValues } from '../../types/phaseOptions';

export const getResourceRequested = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const phaseJob = getPhaseByType(theJob.jobPhases, phaseType);
    if (!phaseJob) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(phaseJob, theJob)) return SKIPPED_PHASE_DISPLAY;
    if (phaseJob.dateRequested) {
      const dateRequested = dateWithoutServerTimezone(phaseJob.dateRequested);
      return `${
        dateRequested.getMonth() + 1
      }/${dateRequested.getDate()}/${dateRequested.getFullYear()}`;
    }
    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getResourceAllocated = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
  currentContractor = false,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;

    if (currentContractor && theJob?.job?.reqBumpoutTimesheet) {
      return jobPhase.contractorName || '';
    } else {
      return jobPhase.initialContractorName || '';
    }
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getSupervisorApproved = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    return jobPhase.approvingUserFullName || '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getRequestedBy = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    return jobPhase.requestingUserFullName || '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getStartDate = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase: any = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;

    if (
      (jobPhase.priority === 500 || jobPhase.priority === 400) &&
      jobPhase.approved &&
      jobPhase.customDateStart
    ) {
      const customDateStart = dateWithoutServerTimezone(
        jobPhase.customDateStart,
      );
      return `${
        customDateStart.getMonth() + 1
      }/${customDateStart.getDate()}/${customDateStart.getFullYear()}`;
    }

    if (jobPhase.dateStart) {
      const dateStart = dateWithoutServerTimezone(jobPhase.dateStart);
      return `${
        dateStart.getMonth() + 1
      }/${dateStart.getDate()}/${dateStart.getFullYear()}`;
    }

    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const getApprovedDate = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (!jobPhase) return SKIPPED_PHASE_DISPLAY;
    if (isStartingApprovedPhase(jobPhase, theJob)) return SKIPPED_PHASE_DISPLAY;
    if (jobPhase.dateApproved) {
      const dateStart = dateWithoutServerTimezone(jobPhase.dateApproved);
      return `${
        dateStart.getMonth() + 1
      }/${dateStart.getDate()}/${dateStart.getFullYear()}`;
    }
    return '';
  } catch (error) {
    return SKIPPED_PHASE_DISPLAY;
  }
};

export const showCameraPhase = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (jobPhase) {
      return (
        !jobPhase.approved && jobPhase.contractorName && jobPhase.requested
      );
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const isApproved = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    if (jobPhase) {
      return jobPhase.approved;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const showPhaseImages = (
  phaseType: PhaseTypeEnum,
  theJob: any,
  _getPhase?: any,
) => {
  try {
    const jobPhase = getPhaseByType(theJob?.jobPhases, phaseType);
    return (
      jobPhase &&
      (jobPhase.hasPhotos ||
        (jobPhase.jobPhasePhotos && jobPhase.jobPhasePhotos.length > 0))
    );
  } catch (error) {
    return false;
  }
};

interface UseTimelineLogicProps {
  geoCoords?: [number, number] | null;
  geoAccuracy?: number | null;
  geoLoading?: boolean;
  requestLocation?: () => void;
  geoLocationError?: string;
  showBump?: (bumpType: PhaseTypeEnum) => boolean;
  getNextStatusText?: (theJob: any) => string;
}

export const useTimelineLogic = ({
  geoCoords = null,
  geoAccuracy = null,
  geoLoading = false,
  requestLocation,
  geoLocationError,
  showBump,
  getNextStatusText: getNextStatusTextProp,
}: UseTimelineLogicProps) => {
  const { selectedJob, refreshJobPhases, applyTimelinePayload } =
    MasterSheetContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const config = useWorkflowConfig();
  const [garageEnabled] = useState(true);

  const [openContractorModal, setOpenContractorModal] = useState(false);
  const [selectedJobPhaseForEdit, setSelectedJobPhaseForEdit] =
    useState<any>(null);

  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedJobPhaseForHistory, setSelectedJobPhaseForHistory] =
    useState<any>(null);

  const [openRequestResource, setOpenRequestResource] = useState(false);
  const [selectedJobPhaseForModal, setSelectedJobPhaseForModal] =
    useState<any>(null);
  const [selectedPhaseType, setSelectedPhaseType] =
    useState<PhaseTypeEnum | null>(null);

  const [openPhotoUpload, setOpenPhotoUpload] = useState(false);
  const [selectedJobPhaseForPhotos, setSelectedJobPhaseForPhotos] =
    useState<any>(null);
  const [isViewPhotosMode, setIsViewPhotosMode] = useState(false);

  const [toggleGarage, setToggleGarage] = useState(false);

  const [openConfirmGoBack, setOpenConfirmGoBack] = useState(false);

  const [openInitialDate, setOpenInitialDate] = useState(false);
  const [openInitialFinishingDate, setOpenInitialFinishingDate] =
    useState(false);
  const [initialDate, setInitialDate] = useState('');
  const [initialFinishingDate, setInitialFinishingDate] = useState('');
  const [changedBySupervisor, setChangedBySupervisor] = useState(false);
  const [changedBySupervisorFinishing, setChangedBySupervisorFinishing] =
    useState(false);

  const phasesToRender = useMemo(() => {
    let phases;
    const jobPhaseStructureType = (selectedJob as any)?.job?.phaseStructureType;

    if (!jobPhaseStructureType) {
      phases = getPhasesForJobType(PhaseStructureTypeEnum.Drywall);
    } else {
      phases = getPhasesForJobType(jobPhaseStructureType);
    }

    if (jobPhaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint) {
      return phases.filter((phaseType: PhaseTypeEnum) =>
        isPhaseIncluded(phaseType, (selectedJob as any)?.job),
      );
    }

    if (showBump) {
      return phases.filter((phaseType: PhaseTypeEnum) => {
        if (
          phaseType === PhaseTypeEnum.Bump1 ||
          phaseType === PhaseTypeEnum.Bump2 ||
          phaseType === PhaseTypeEnum.Bump3
        ) {
          return (selectedJob?.job?.currentJobPhasePriority || 0) >= 600;
        }
        if (
          phaseType === PhaseTypeEnum.Bump4 ||
          phaseType === PhaseTypeEnum.Bump5 ||
          phaseType === PhaseTypeEnum.DrywallTouchUp3 ||
          phaseType === PhaseTypeEnum.CMBluetape ||
          phaseType === PhaseTypeEnum.FinalBluetape
        ) {
          return showBump(phaseType);
        }
        return true;
      });
    }

    return phases;
  }, [
    (selectedJob as any)?.job?.phaseStructureType,
    (selectedJob as any)?.job?.hasTrim,
    (selectedJob as any)?.job?.hasExteriorPaint,
    selectedJob?.job?.currentJobPhasePriority,
    selectedJob?.jobPhase?.approved,
    selectedJob?.jobPhases,
    showBump,
  ]);

  useEffect(() => {
    if (selectedJob?.jobPhases) {
      const garageJobPhase = selectedJob.jobPhases.find(
        (jp: any) => jp.phaseType === PhaseTypeEnum.Garage,
      );
      if (garageJobPhase) {
        const currentPriority = selectedJob.job?.currentJobPhasePriority;
        // If currently on Garage phase (priority 200), force toggle ON
        // even if garageFirst was false at creation
        const isOnGaragePhase = currentPriority === 200;
        setToggleGarage(garageJobPhase.required || isOnGaragePhase);
      }
    }
  }, [selectedJob]);

  useEffect(() => {
    if (selectedJob?.jobPhases) {
      const sprayPhase: any = selectedJob.jobPhases.find(
        jobPhase => jobPhase.priority === 500,
      );
      const finishingPhase = selectedJob.jobPhases.find(
        jobPhase => jobPhase.priority === 400,
      );
      if (sprayPhase) {
        setInitialDate(sprayPhase.initialDateStart);
        setChangedBySupervisor(!!sprayPhase.customDateStart);
      }
      if (finishingPhase) {
        setInitialFinishingDate(finishingPhase.initialDateStart);
        setChangedBySupervisorFinishing(!!finishingPhase.customDateStart);
      }
    }
  }, [selectedJob]);

  const onChangeGarage = (
    theJob,
    toggleGarage,
    refreshJobPhases,
    setToggleGarage,
    _getPhase,
  ) => {
    const garagePhase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Garage);
    if (garagePhase) {
      const params = {
        id: garagePhase.id,
        data: {
          required: !toggleGarage,
        },
        previousData: garagePhase,
      };
      dataProvider.update('job-phase', params).then(_response => {
        refreshJobPhases();
        setToggleGarage(!toggleGarage);
      });
    }
  };

  const handleGoBack = () => {
    setOpenConfirmGoBack(true);
  };

  const goBackJob = async () => {
    if (!selectedJob?.jobPhase || !selectedJob?.jobPhases || !selectedJob?.job)
      return;

    const currentPhaseType = selectedJob.jobPhase.phaseType;
    if (!currentPhaseType) return;

    const jobPhase = getPhaseByType(selectedJob.jobPhases, currentPhaseType);
    if (!jobPhase || !jobPhase.id) return;

    const params = {
      id: jobPhase.id,
      data: {} as any,
      previousData: jobPhase,
    };

    if (jobPhase.approved || (!jobPhase.contractorId && !jobPhase.requested)) {
      return;
    }

    if (jobPhase?.phaseType && BUMP_PHASES.includes(jobPhase.phaseType)) {
      params.data.contractorId = null;
      params.data.requested = false;
    } else {
      if (jobPhase.contractorId) {
        params.data.contractorId = null;
      } else if (jobPhase.requested) {
        params.data.requested = false;
      }
    }

    try {
      await dataProvider.update('job-phase', params);

      // Interior/exterior/trim option flags and their history are cleared by
      // the backend on the phase PATCH above (JobPhaseCrudService clears them
      // for requested=false and contractorId=null), so no PATCH job here.

      notify('Go back with success!');
      await refreshJobPhases();
      setOpenConfirmGoBack(false);
    } catch (error: any) {
      notify(`Go Back error: ${error.message}`, 'warning');
    }
  };

  const handleRequestResource = () => {
    if (selectedJob?.job && selectedJob?.jobPhases) {
      const priority = selectedJob.job.currentJobPhasePriority || 100;

      if (priority === 100) {
        return;
      }

      const currentPhaseType = selectedJob.jobPhase?.phaseType;

      if (currentPhaseType) {
        try {
          const currentPhase = getPhaseByType(
            selectedJob.jobPhases,
            currentPhaseType,
          );

          setSelectedJobPhaseForModal(currentPhase);
          setSelectedPhaseType(currentPhaseType);
          setOpenRequestResource(true);
        } catch (error) {
          console.error('Error getting current phase:', error);
        }
      }
    }
  };

  const handleLogGarage = () => {
    if (selectedJob?.jobPhases) {
      try {
        const garageJobPhase = selectedJob.jobPhases.find(
          (jp: any) => jp.phaseType === PhaseTypeEnum.Garage,
        );

        if (garageJobPhase) {
          setSelectedJobPhaseForModal(garageJobPhase);
          setSelectedPhaseType(PhaseTypeEnum.Garage);
          setOpenRequestResource(true);
        }
      } catch (error) {
        console.error('Error getting garage phase:', error);
      }
    }
  };

  const handleApprove = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return;

    const jobPhase = getPhaseByType(
      selectedJob.jobPhases,
      phaseType as PhaseTypeEnum,
    );

    if (jobPhase) {
      setSelectedJobPhaseForPhotos(jobPhase);
      setIsViewPhotosMode(false);
      setOpenPhotoUpload(true);
    }
  };

  const handleEditContractor = (phaseType: PhaseTypeEnum) => {
    if (!selectedJob?.jobPhases) return;

    try {
      const jobPhase = getPhaseByType(selectedJob.jobPhases, phaseType);
      setSelectedJobPhaseForEdit(jobPhase);
      setOpenContractorModal(true);
    } catch (error) {
      console.error('Error getting job phase:', error);
    }
  };

  const handleViewPhotos = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return;

    const jobPhase = getPhaseByType(
      selectedJob.jobPhases,
      phaseType as PhaseTypeEnum,
    );

    if (jobPhase) {
      setSelectedJobPhaseForPhotos(jobPhase);
      setIsViewPhotosMode(true);
      setOpenPhotoUpload(true);
    }
  };

  const handleHistoryModal = async (phaseType: PhaseTypeEnum) => {
    if (!selectedJob?.jobPhases) return;

    try {
      const jobPhase = getPhaseByType(selectedJob.jobPhases, phaseType);

      const contractorHistory = await dataProvider.getList<any>(
        'job-phase-contractor-history-report',
        {
          filter: { jobPhaseId: jobPhase.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
        },
      );

      const jobPhaseWithHistory = {
        ...jobPhase,
        jobPhaseContractorHistories: contractorHistory.data,
      };

      setSelectedJobPhaseForHistory(jobPhaseWithHistory);
      setOpenHistoryModal(true);
    } catch (error) {
      console.error('Error loading contractor history:', error);
      notify(`Error loading contractor history: ${error.message}`, 'error');
    }
  };

  const needStrike = (phaseType: string) => {
    if (!selectedJob?.jobPhases) return false;

    const jobPhaseFound = selectedJob.jobPhases.find(
      (jobPhase: any) => jobPhase.phaseType === phaseType,
    );

    if (!jobPhaseFound) return false;

    return parseInt(jobPhaseFound.contractorHistoryCount || '0') > 1;
  };

  const getNextStatusText = (theJob: any) => {
    const currentPhase = theJob?.jobPhase;
    if (!currentPhase) return 'No Phase';

    if (currentPhase?.phaseType === PhaseTypeEnum.Materials) {
      return 'Materials Sign Off';
    }

    // Check if this is a log phase
    const currentPhaseType: PhaseTypeEnum | undefined = currentPhase.phaseType;
    const modalConfig = currentPhaseType
      ? getModalConfig(currentPhaseType)
      : null;
    const isLogPhase =
      currentPhaseType && modalConfig && modalConfig.modalType === 'log';

    if (currentPhase.approved === true) {
      return `${currentPhase?.phaseName}: Approved`;
    }

    if (currentPhase.requested === true) {
      if (currentPhase.contractorId) {
        return `${currentPhase?.phaseName}: Resources Allocated`;
      }
      return `${currentPhase?.phaseName}: Resources Requested`;
    } else {
      // For log phases, show "Log Contractor" — but bump phases in bumpout timesheet flow use "Request Resource"
      const isBumpPhase =
        currentPhaseType && BUMP_PHASES.includes(currentPhaseType);
      const isBumpoutActive = isBumpPhase && theJob?.job?.reqBumpoutTimesheet;
      const actionText =
        isLogPhase && !isBumpoutActive ? 'Log Contractor' : 'Request Resource';
      return `${currentPhase?.phaseName}: ${actionText}`;
    }
  };

  const checkRequestResourcePermission = () => {
    if (selectedJob?.jobPhase?.contractorId && !selectedJob.jobPhase.approved) {
      return hasPermission(CAN_REQUEST_RESOURCE_ASSIGNED);
    }
    return hasPermission(CAN_REQUEST_RESOURCE);
  };

  const isRequestResourceDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    return (
      selectedJob.jobPhase.priority < 200 ||
      selectedJob.jobPhase.approved === true ||
      selectedJob.jobPhase.requested === true
    );
  };

  const getRequestButtonLabel = () => {
    const currentPhaseType = selectedJob?.jobPhase?.phaseType;

    if (!currentPhaseType) return 'Request Resource';

    const labels = config.timeline.bumpRequestLabels;

    // If config has labels (Drywall), use them
    if (labels) {
      if (currentPhaseType === PhaseTypeEnum.Garage) {
        return labels.garage || 'Log Garage';
      }
      if (BUMP_PHASES.includes(currentPhaseType)) {
        return selectedJob?.job?.reqBumpoutTimesheet
          ? labels.bumpTimesheet || 'Request BumpOut'
          : labels.bumpDefault || 'Log BumpOut';
      }
      return 'Request Resource';
    }

    // D&P fallback: use modal config
    const modalConfig = getModalConfig(currentPhaseType);
    if (modalConfig.modalType === 'log') {
      return 'Log';
    }

    return 'Request Resource';
  };

  const goBackWarningMessage = useMemo(() => {
    const warnings = config.timeline.goBackWarnings;
    if (!warnings || Object.keys(warnings).length === 0) {
      return 'Are you sure that you want to go back?';
    }

    const currentPhaseType = selectedJob?.jobPhase?.phaseType;
    const isBumpPhase =
      currentPhaseType && BUMP_PHASES.includes(currentPhaseType);
    const isBumpoutActive = selectedJob?.job?.reqBumpoutTimesheet;

    if (isBumpPhase && isBumpoutActive && warnings.bump) {
      return warnings.bump;
    }

    return warnings.default || 'Are you sure that you want to go back?';
  }, [
    config.timeline.goBackWarnings,
    selectedJob?.jobPhase?.phaseType,
    selectedJob?.job?.reqBumpoutTimesheet,
  ]);

  const isGoBackDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    const isJobPhaseApproved = selectedJob.jobPhase.approved;
    const hasContractor = selectedJob.jobPhase.contractorId;
    const isRequested = selectedJob.jobPhase.requested;

    return (
      isJobPhaseApproved ||
      (!hasContractor && !isRequested) ||
      selectedJob.jobPhase.priority < 200
    );
  };

  const isGarageDisabled = () => {
    if (!selectedJob?.jobPhase) return true;

    const jobPhase = selectedJob.jobPhase;
    const garagePhase = getPhaseByType(
      selectedJob.jobPhases,
      PhaseTypeEnum.Garage,
    );

    if (
      jobPhase.priority < 200 ||
      jobPhase.priority > 300 ||
      jobPhase.requested === true ||
      (garagePhase && garagePhase.dateApproved)
    ) {
      return true;
    }

    return false;
  };

  const approveJob = async (options: PhaseApprovalOptionValues) => {
    const jobPhase = selectedJobPhaseForPhotos;
    if (jobPhase && selectedJob?.job) {
      const isTrimOptionApproval = Boolean(
        selectedJob.job.hasTrimOptions && isTrimOptionPhase(jobPhase.phaseType),
      );
      const isPaintPhaseWithOptions =
        jobPhase.phaseType === PhaseTypeEnum.FinalPaint ||
        jobPhase.phaseType === PhaseTypeEnum.InteriorPaint;

      // Single transactional request: POST job-phase/:id/approve approves
      // the phase, finalizes trim and records the paint options on the job
      // together (the old PATCH job-phase → PATCH job pair could leave the
      // phase advanced with stale job flags).
      const payload: JobPhaseApproveDto = {};

      if (isTrimOptionApproval) {
        payload.trimOptionSelected = options.trimOptionSelected;
      }

      if (geoCoords) {
        payload.approvedGeo = { type: 'Point', coordinates: geoCoords };
        payload.approvedGeoAccuracy = geoAccuracy ?? undefined;
      }

      if (isPaintPhaseWithOptions) {
        if (
          selectedJob.job.interiorOptionsFinished !==
          options.interiorOptionsCompleted
        ) {
          payload.interiorOptionsCompleted = options.interiorOptionsCompleted;
        }
        if (
          selectedJob.job.exteriorOptionsFinished !==
          options.exteriorOptionsCompleted
        ) {
          payload.exteriorOptionsCompleted = options.exteriorOptionsCompleted;
        }
      }

      try {
        const { data } = await axiosInstance.post<JobPhaseTimelineResponseDto>(
          `job-phase/${jobPhase.id}/approve`,
          payload,
        );

        applyTimelinePayload(data);
        notify(
          isTrimOptionApproval || isExteriorPaintPhase(jobPhase.phaseType)
            ? 'Phase approved successfully.'
            : 'Job Approved!',
        );
        setOpenPhotoUpload(false);
      } catch (error: any) {
        const message = extractErrorMessage(error, 'Request failed');
        if (isTrimOptionApproval) {
          notify('Unable to Approve Phase. Please try again later.', 'warning');
        } else {
          notify(`Approve Job Error: ${message}`, 'warning');
        }
      }
    }
  };

  return {
    selectedJob,
    refreshJobPhases,
    applyTimelinePayload,
    hasPermission,
    garageEnabled,
    openContractorModal,
    setOpenContractorModal,
    selectedJobPhaseForEdit,
    openHistoryModal,
    setOpenHistoryModal,
    selectedJobPhaseForHistory,
    openRequestResource,
    setOpenRequestResource,
    selectedJobPhaseForModal,
    setSelectedJobPhaseForModal,
    selectedPhaseType,
    setSelectedPhaseType,
    openPhotoUpload,
    setOpenPhotoUpload,
    selectedJobPhaseForPhotos,
    isViewPhotosMode,
    toggleGarage,
    setToggleGarage,
    openConfirmGoBack,
    setOpenConfirmGoBack,
    openInitialDate,
    setOpenInitialDate,
    openInitialFinishingDate,
    setOpenInitialFinishingDate,
    initialDate,
    initialFinishingDate,
    changedBySupervisor,
    changedBySupervisorFinishing,
    getPhase: getPhaseByType,
    phasesToRender,
    onChangeGarage,
    handleGoBack,
    goBackJob,
    handleRequestResource,
    handleLogGarage,
    handleApprove,
    handleEditContractor,
    handleViewPhotos,
    handleHistoryModal,
    needStrike,
    getNextStatusText,
    checkRequestResourcePermission,
    isRequestResourceDisabled,
    getRequestButtonLabel,
    isGoBackDisabled,
    isGarageDisabled,
    approveJob,
    geoCoords,
    geoAccuracy,
    geoLoading,
    requestLocation,
    geoLocationError,
    getNextStatusTextProp,
    goBackWarningMessage,
  };
};
